<script setup>
import { ref, watch } from 'vue';
import LocationSelect from './LocationSelect.vue';
import SvgIcon from './SvgIcon.vue';
import { writeLocationCookie } from '@/shared/locationCookie.js';
import { reloadForLocation } from '@/shared/locationReload.js';
import { activateLocationScopedPage } from '@/shared/searchBarStore.js';

/*
 * A standalone "which spa are you shopping at" control, for any page that needs to offer the
 * choice without depending on the customer finding the header's Book/Gift/Redeem tabs.
 *
 * The whole widget is LocationSelect - the same dropdown the header's Where field uses,
 * nearest-to-me lookup included - plus the two things that make a choice stick: the cookie,
 * and a reload. Both are shared modules rather than copies, because the header does exactly
 * the same two things and the two controls can be on one page together.
 *
 * Staying in sync with the header is the point, and the reload is what guarantees it: change
 * either control and the page comes back with both seeded from the same cookie. There is no
 * live handshake to get wrong.
 *
 * Reloading rather than telling the host page to refetch: a host that forgot to wire an event
 * up would silently do nothing, which is the failure this component exists to fix. Worth
 * revisiting for a snappier in-place refresh once there is a second consumer to design for.
 */

const props = defineProps({
    locations: { type: Array, required: true },
    nearestLocationsUrl: { type: String, required: true },
    /*
     * The spa the page was rendered for, from LocationService::bookingLocation() - so this
     * control starts on the same answer the rest of the page was built from, and on the same
     * answer the header's Where field seeds itself with. Null where the customer has chosen
     * none, which is a normal state: the select shows its placeholder.
     */
    locationId: { type: [Number, null], default: null },
});

/*
 * Tells the header's search bar that this page's content is priced against the chosen spa, so
 * changing it THERE reloads too. Without this the two controls would fall out of step in one
 * direction only: this widget would refresh the page and the header would quietly not.
 *
 * The claim is true by construction - a page only carries this control because its content
 * depends on the spa - which is why it is asserted here rather than left to each host page to
 * remember.
 */
activateLocationScopedPage();

const selected = ref(props.locationId);

// What the page on screen was rendered for, captured once so the watcher can tell a real
// change from the seed. Same guard, and the same reason, as SearchBar.vue's own.
const renderedLocationId = props.locationId;

// Deliberately not immediate: an immediate run would write the cookie and reload on arrival,
// forever. Only a change the customer actually made gets that far.
watch(selected, (locationId) => {
    if (! locationId || locationId === renderedLocationId) {
        return;
    }

    writeLocationCookie(locationId);
    reloadForLocation();
});
</script>

<template>
    <div class="location_switcher">
        <p class="location_switcher_label">Showing availability for</p>
        <!-- Two elements, mirroring the search bar's own pill > field structure. The pill
             owns the chrome and the field owns the inset and the control's look, exactly as
             they are split there - one element doing both means the pill's `padding`
             shorthand resetting whatever the field asked for. -->
        <div class="location_switcher_pill">
            <div class="location_switcher_field">
                <!-- The field's own "Where" label is hidden, not removed: the sentence this
                     sits in already says what the control is, but aria-labelledby needs it. -->
                <LocationSelect
                    v-model="selected"
                    :locations="locations"
                    :nearest-locations-url="nearestLocationsUrl"
                    hide-label
                >
                    <!-- The same pencil the search bar shows on mobile once it is reading a
                         search back: something to amend rather than to start. Inside the
                         trigger, so it is part of the button; decorative to assistive tech,
                         which already has the field's own label. -->
                    <template #trigger-suffix>
                        <span class="location_switcher_icon" aria-hidden="true">
                            <SvgIcon name="edit" />
                        </span>
                    </template>
                </LocationSelect>
            </div>
        </div>
    </div>
</template>
